
import type { ChecklistItem } from '@/hooks/useChecklistItems';

export interface EnhancedChecklistStats {
  totalItems: number;
  completedItems: number;
  completionPercentage: number;
  hasOverdueItems: boolean;
  hasHighPriorityItems: boolean;
  assignedUsers: string[];
  checklistNames: string[];
}

interface ChecklistMetadata {
  id: string;
  name: string;
}

export const calculateEnhancedChecklistStats = (
  checklistItems: ChecklistItem[],
  checklistMetadata: ChecklistMetadata[] = []
): EnhancedChecklistStats => {
  if (!checklistItems || checklistItems.length === 0) {
    return {
      totalItems: 0,
      completedItems: 0,
      completionPercentage: 0,
      hasOverdueItems: false,
      hasHighPriorityItems: false,
      assignedUsers: [],
      checklistNames: [],
    };
  }

  const totalItems = checklistItems.length;
  const completedItems = checklistItems.filter(item => item.completed).length;
  const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const hasOverdueItems = checklistItems.some(item => {
    if (!item.due_date || item.completed) return false;
    const dueDate = new Date(item.due_date);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  });

  const hasHighPriorityItems = checklistItems.some(item => 
    item.priority === 'high' && !item.completed
  );

  const assignedUsers = [...new Set(
    checklistItems
      .filter(item => item.assigned_profile?.full_name || item.assigned_profile?.username)
      .map(item => item.assigned_profile?.full_name || item.assigned_profile?.username || '')
      .filter(name => name.length > 0)
  )];

  // Get unique checklist IDs and map them to names using metadata
  const uniqueChecklistIds = [...new Set(
    checklistItems.map(item => item.checklist_id).filter(Boolean)
  )];

    const checklistNames = uniqueChecklistIds.map(checklistId => {
        let parsedChecklist = [];

        try {
            parsedChecklist = JSON.parse(checklistMetadata.toString());
        } catch (e) {
            return 'Unnamed Checklist';
        }

        if (!Array.isArray(parsedChecklist) || parsedChecklist.length === 0)
            return 'Unnamed Checklist';
        //console.log("Checklist data:", { checkListId: checklistId, checklistMetadata: checklistMetadata, checklistMetadataType: typeof checklistMetadata, parsedChecklist: parsedChecklist });
    const metadata = parsedChecklist?.find(cl => cl.id === checklistId);
    return metadata?.name || 'Unnamed Checklist';
  });

  return {
    totalItems,
    completedItems,
    completionPercentage,
    hasOverdueItems,
    hasHighPriorityItems,
    assignedUsers,
    checklistNames,
  };
};

export const isItemOverdue = (item: ChecklistItem): boolean => {
  if (!item.due_date || item.completed) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(item.due_date);
  dueDate.setHours(0, 0, 0, 0);
  return dueDate < today;
};

export const isItemDueSoon = (item: ChecklistItem): boolean => {
  if (!item.due_date || item.completed) return false;
  const due = new Date(item.due_date);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due <= tomorrow && due >= today;
};
